import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import DropdownSelect, { DropdownItem } from 'app/components/DropdownSelect';
import NavigationDefault from 'app/components/Navigation/NavigationDefault';
import { NavigationDetail } from 'app/components/Navigation/core/types';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import Typography from 'app/components/Typography';

import DeviceConnection from 'app/modules/alerts/components/DeviceConnection';
import { DROPDOWN_BUTTON_TEXT, DROPDOWN_SELECT_QUERY_KEY } from 'app/modules/alerts/core/constant';
import {
  getAlertSuppression,
  getAlertSuppression25,
  getTotalUnreadAlert,
  updateAlertSuppression,
  updateAlertSuppression25,
  updateMarkAllAlert,
} from 'app/modules/alerts/core/request';
import { Alert, AlertType, SuppressionResponse, TAB_ALERTS } from 'app/modules/alerts/core/type';
import { queryToTabMap, tabToQueryMap } from 'app/modules/alerts/core/util';
import { useAlertStore } from 'app/modules/alerts/store';
import { Header } from 'app/modules/notifications/NotificationPage';

import { isNumber } from 'lodash';

import queryString from 'query-string';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const ItemExpiry = lazy(() => import('app/modules/alerts/components/ItemExpiry'));
const ReachCriticalLevel = lazy(() => import('app/modules/alerts/components/ReachCriticalLevel'));
const RogueDevice = lazy(() => import('app/modules/alerts/components/RogueDevice'));

const AlertsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = queryString.parse(location.search);
  const tabQuery = query.tab as string;

  const [tab, setTab] = useState<TAB_ALERTS>(() => {
    const mappedTab = queryToTabMap[tabQuery];
    return isNumber(mappedTab) ? mappedTab : TAB_ALERTS.DEVICE_CONNECTION;
  });

  const alertType = useMemo(
    () =>
      ({
        [TAB_ALERTS.DEVICE_CONNECTION]: AlertType['Rack Failed Connection'],
        [TAB_ALERTS.ITEM_EXPIRY]: AlertType['Shelf Expiry'],
        [TAB_ALERTS.REACH_CRITICAL_LEVEL]: AlertType['Reach Critical Level'],
        [TAB_ALERTS.ABNORMAL_CONSUMPTION]: AlertType['Abnormal Consumption'],
        [TAB_ALERTS.ROGUE_DEVICE]: AlertType['Rogue Device'],
      }[tab]),
    [tab],
  );

  const unReadCount = useAlertStore((s) => s.state.unReadCount);
  const fetched = useAlertStore((s) => s.state.fetched);
  const organization = useOrganizationStore((state) => state.state.organization);
  const alertGroup = useAlertStore((s) => s.state.alertGroup);

  const { data: alertSuppressionItems = [] } = useQuery<SuppressionResponse[]>({
    queryKey: ['alertSuppression', alertType, organization?.id],
    queryFn: () => getAlertSuppression(alertType),
    staleTime: 30000,
    // Rogue Device alerts have nothing to suppress against (no registered inventory row) —
    // skip the request entirely rather than hit the backend's empty-array fallback.
    enabled: !!alertType && !!organization?.id && tab !== TAB_ALERTS.ROGUE_DEVICE,
  });

  // SIMS 2.5's own suppression state — independent of the SIMS 2.0 query above.
  const { data: alertSuppression25Items = [] } = useQuery<SuppressionResponse[]>({
    queryKey: ['alertSuppression25', alertType, organization?.id],
    queryFn: () => getAlertSuppression25(alertType),
    staleTime: 30000,
    enabled: !!alertType && !!organization?.id && tab !== TAB_ALERTS.ROGUE_DEVICE,
  });

  useEffect(() => {
    if (alertSuppressionItems.length > 0) {
      useAlertStore.getState().setState({
        alertSuppressionItems,
      });
    }
  }, [alertSuppressionItems]);

  useEffect(() => {
    if (alertSuppression25Items.length > 0) {
      useAlertStore.getState().setState({
        alertSuppression25Items,
      });
    }
  }, [alertSuppression25Items]);

  const dropdownItems = useMemo(
    () =>
      alertSuppressionItems?.map((item) => ({
        id: item.id,
        title: item.name,
        subtitle:
          tab === TAB_ALERTS.DEVICE_CONNECTION
            ? `${item.active ? 'Suppressed' : 'Active'}`
            : `SKU: ${item.sku} - ${item.active ? 'Suppressed' : 'Active'}`,
        sku: item.sku,
        selected: item.active,
      })),
    [alertSuppressionItems, tab],
  );

  const dropdownItems25 = useMemo(
    () =>
      alertSuppression25Items?.map((item) => ({
        id: item.id,
        title: item.name,
        subtitle:
          tab === TAB_ALERTS.DEVICE_CONNECTION
            ? `${item.active ? 'Suppressed' : 'Active'}`
            : `SKU: ${item.sku} - ${item.active ? 'Suppressed' : 'Active'}`,
        sku: item.sku,
        selected: item.active,
      })),
    [alertSuppression25Items, tab],
  );

  const { mutateAsync: updateAlertSuppressionMutation } = useMutation<
    void,
    Error,
    { relation: string | number; active: boolean }[]
  >({
    mutationKey: ['update-alert-suppression', alertType],
    mutationFn: (payload: { relation: string | number; active: boolean }[]) =>
      updateAlertSuppression(alertType, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['alertSuppression', alertType, organization?.id]);
    },
  });

  // SIMS 2.5's own suppression — independent mutation, independent table on the backend.
  const { mutateAsync: updateAlertSuppression25Mutation } = useMutation<
    void,
    Error,
    { relation: string | number; active: boolean }[]
  >({
    mutationKey: ['update-alert-suppression-2-5', alertType],
    mutationFn: (payload: { relation: string | number; active: boolean }[]) =>
      updateAlertSuppression25(alertType, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['alertSuppression25', alertType, organization?.id]);
    },
  });

  useEffect(() => {
    const mappedTab = queryToTabMap[tabQuery];

    if (isNumber(mappedTab)) {
      setTab(mappedTab);
      return;
    }

    navigate(`?tab=${tabToQueryMap[TAB_ALERTS.DEVICE_CONNECTION]}`);
  }, [tabQuery]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries(['alerts']);
      queryClient.setQueryData(['alerts'], undefined);
      useAlertStore.getState().setState({
        fetched: false,
        total: 0,
      });
    };
  }, []);

  const navigation: NavigationDetail[] = [
    {
      title: `Device connection (${alertGroup?.['Rack Failed Connection']?.total || 0})`,
      value: TAB_ALERTS.DEVICE_CONNECTION,
      content: <DeviceConnection tab={tab} />,
    },
    {
      title: `Item expiry (${alertGroup?.['Shelf Expiry']?.total || 0})`,
      value: TAB_ALERTS.ITEM_EXPIRY,
      content: (
        <Suspense fallback={<LoadingCard />}>
          <ItemExpiry tab={tab} />
        </Suspense>
      ),
    },
    {
      title: `Reach critical level (${alertGroup?.['Reach Critical Level']?.total || 0})`,
      value: TAB_ALERTS.REACH_CRITICAL_LEVEL,
      content: (
        <Suspense fallback={<LoadingCard />}>
          <ReachCriticalLevel tab={tab} />
        </Suspense>
      ),
    },
    // {
    //   title: `Abnormal consumption (${alertGroup?.['Abnormal Consumption']?.total || 0})`,
    //   value: TAB_ALERTS.ABNORMAL_CONSUMPTION,
    //   content: (
    //     <Suspense fallback={<LoadingCard />}>
    //       <AbnormalConsumption />
    //     </Suspense>
    //   ),
    // },
    {
      title: `Rogue device (${alertGroup?.['Rogue Device']?.total || 0})`,
      value: TAB_ALERTS.ROGUE_DEVICE,
      content: (
        <Suspense fallback={<LoadingCard />}>
          <RogueDevice tab={tab} />
        </Suspense>
      ),
    },
  ];

  const currentTab = navigation.find((nav) => nav.value === tab);

  const handleChangeNavigation = (newValue: TAB_ALERTS) => () => {
    const queryValue = tabToQueryMap[newValue] ?? String(newValue);
    navigate(`?tab=${queryValue}`);
  };

  const handleMarkAllAsRead = async () => {
    await updateMarkAllAlert(organization?.id ?? 0);
    const response = await updateMarkAllAlert(organization?.id ?? 0);

    if (response) {
      useAlertStore.getState().setState({
        unReadCount: 0,
      });

      queryClient
        .getQueryCache()
        .findAll(['alerts'])
        .forEach((queryAlert) => {
          const data: any = queryAlert.state.data;
          if (data && data.pages) {
            queryClient.setQueryData(queryAlert.queryKey, {
              ...data,
              pages: data.pages.map((page: any) => ({
                ...page,
                items: page.items?.map((item: Alert) => ({
                  ...item,
                  is_read: true,
                })),
              })),
            });
          }
        });

      getTotalUnreadAlert();
    }
  };

  const handleFilterSubmit = useCallback(
    async (selectedItems: DropdownItem[]): Promise<boolean> => {
      try {
        const selectedIdMap = new Set(selectedItems.map(({ id }) => id));

        const changedItems: SuppressionResponse[] = alertSuppressionItems.filter(
          ({ id, active }) => {
            const isNowActive = selectedIdMap.has(id);
            return active !== isNowActive;
          },
        );

        const payload = changedItems.map(({ id }) => ({
          relation: id,
          active: selectedIdMap.has(id),
        }));

        await updateAlertSuppressionMutation(payload);
        return true;
      } catch (error) {
        return false;
      }
    },
    [alertSuppressionItems, updateAlertSuppressionMutation],
  );

  const handleFilterSubmit25 = useCallback(
    async (selectedItems: DropdownItem[]): Promise<boolean> => {
      try {
        const selectedIdMap = new Set(selectedItems.map(({ id }) => id));

        const changedItems: SuppressionResponse[] = alertSuppression25Items.filter(
          ({ id, active }) => {
            const isNowActive = selectedIdMap.has(id);
            return active !== isNowActive;
          },
        );

        const payload = changedItems.map(({ id }) => ({
          relation: id,
          active: selectedIdMap.has(id),
        }));

        await updateAlertSuppression25Mutation(payload);
        return true;
      } catch (error) {
        return false;
      }
    },
    [alertSuppression25Items, updateAlertSuppression25Mutation],
  );

  return (
    <>
      <Header className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-8">
          <Typography variant="heading-6">Alerts</Typography>
          {unReadCount > 0 && fetched && (
            <Typography
              variant="body-1"
              color="primary"
              className="title"
              onClick={handleMarkAllAsRead}
            >
              Mark all as read
            </Typography>
          )}
        </div>

        {/* Rogue Device alerts have nothing to suppress against (no registered inventory
            row) — the suppression dropdowns don't apply to this tab. */}
        {tab !== TAB_ALERTS.ROGUE_DEVICE && (
        <div className="d-flex align-items-center gap-3">
          <DropdownSelect
            buttonText={`${DROPDOWN_BUTTON_TEXT(tab)} (2.0)`}
            title="Alert Suppression — SIMS 2.0"
            subtitle="Select items/devices to suppress alerts on SIMS 2.0"
            queryKey={DROPDOWN_SELECT_QUERY_KEY[tab]}
            onSubmit={handleFilterSubmit}
            items={dropdownItems}
            buttonSubmitText="Suppress"
          />
          <DropdownSelect
            buttonText={`${DROPDOWN_BUTTON_TEXT(tab)} (2.5)`}
            title="Alert Suppression — SIMS 2.5"
            subtitle="Select items/devices to suppress alerts on SIMS 2.5"
            queryKey={`${DROPDOWN_SELECT_QUERY_KEY[tab]}-2-5`}
            onSubmit={handleFilterSubmit25}
            items={dropdownItems25}
            buttonSubmitText="Suppress"
          />
        </div>
        )}
      </Header>
      <NavigationDefault
        mainStyle={{ borderBottom: '1px solid var(--neutral-4)', margin: '0 0 30px' }}
        valueActive={tab}
        navigation={navigation}
        onChangeNavigation={handleChangeNavigation}
      />
      {currentTab?.content}
    </>
  );
};

export default AlertsPage;
